import LocalStorageService from "@application/common/local-storage-service";
import { HubConnectionBuilder, LogLevel, } from "@microsoft/signalr";
import { vdkEmHappinessPortalApi } from "./vdk-em-happiness-portal-api";
async function startConnectionWithRetry(connection, maxAttempts = 50, initialDelayMs = 500, shouldCancel) {
    let attempt = 0;
    let delay = initialDelayMs;
    // Attempt initial connection with basic retry/backoff;
    // once connected, withAutomaticReconnect will handle transient drops.
    // eslint-disable-next-line no-constant-condition
    while (true) {
        // Abort if caller requested cancellation (e.g., user logged out)
        if (shouldCancel?.()) {
            throw new Error("CANCELLED");
        }
        try {
            await connection.start();
            return;
        }
        catch (err) {
            console.log(`Notifications hub connection attempt ${attempt + 1} failed:`, err);
            attempt += 1;
            if (attempt >= maxAttempts) {
                throw err;
            }
            // Abort between retries if requested
            if (shouldCancel?.()) {
                throw new Error("CANCELLED");
            }
            await new Promise((resolve) => setTimeout(resolve, delay));
            delay = Math.min(delay * 2, 8000);
        }
    }
}
export async function createNotificationsHub({ apiBase, getToken, callbacks, shouldCancel, }) {
    const token = getToken?.();
    if (!token) {
        throw new Error("Missing JWT token for notifications hub bootstrap.");
    }
    // Fetch bootstrap via axios instance (sends both Authorization and XAuthorization)
    const { data: bootstrap } = await vdkEmHappinessPortalApi.get("notifications/initializeNotification");
    if (!bootstrap?.hubUrl) {
        throw new Error("Invalid bootstrap response: missing hubUrl.");
    }
    // Join apiBase and hubUrl robustly
    const base = (apiBase || "").replace(/\/+$/, "");
    const hubPath = bootstrap.hubUrl.startsWith("/")
        ? bootstrap.hubUrl
        : `/${bootstrap.hubUrl}`;
    const hubUrl = `${base}${hubPath}`;
    const connection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
        accessTokenFactory: () => LocalStorageService.getSignalRToken() ?? ""
    })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();
    // Backend → client events
    connection.on("connectionStatus", (status) => {
        callbacks?.onConnectionStateChanged?.(status);
    });
    // Server → client events
    connection.on("notificationsUpdated", () => {
        callbacks?.onNotificationsUpdated?.();
    });
    // Be tolerant to different casing/event names from server
    connection.on("NotificationsUpdated", () => {
        callbacks?.onNotificationsUpdated?.();
    });
    connection.on("notificationCreated", () => {
        callbacks?.onNotificationsUpdated?.();
    });
    connection.on("NotificationCreated", () => {
        callbacks?.onNotificationsUpdated?.();
    });
    connection.on("unreadCountUpdated", (count) => {
        callbacks?.onUnreadCountUpdated?.(count);
    });
    connection.on("notificationRead", (id) => {
        callbacks?.onNotificationRead?.(id);
    });
    connection.on("notificationsMarkedAllRead", (ids) => {
        callbacks?.onNotificationsMarkedAllRead?.(ids);
    });
    // Frontend lifecycle events
    connection.onreconnecting(() => {
        callbacks?.onConnectionStateChanged?.("reconnecting");
    });
    connection.onreconnected(() => {
        callbacks?.onConnectionStateChanged?.("reconnected");
    });
    connection.onclose(() => {
        callbacks?.onConnectionStateChanged?.("closed");
    });
    callbacks?.onConnectionStateChanged?.("starting");
    await startConnectionWithRetry(connection, undefined, undefined, shouldCancel).then(() => callbacks?.onConnectionStateChanged?.("connected"), () => callbacks?.onConnectionStateChanged?.("error"));
    const api = {
        markAsRead: async (id) => {
            await connection.invoke("MarkAsRead", id);
        },
        markAsUnread: async (id) => {
            await connection.invoke("MarkAsUnread", id);
        },
        markAllAsRead: async () => {
            await connection.invoke("MarkAllAsRead");
        },
        markAllAsUnread: async () => {
            // Support both the correct and legacy method names
            try {
                await connection.invoke("MarkAllAsUnread");
            }
            catch {
                await connection.invoke("MarkAllAsUnRead");
            }
        },
        stop: async () => {
            await connection.stop();
        },
    };
    return { bootstrap, connection, api };
}
